import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { FiCheck, FiX } from "react-icons/fi";
import { motion, AnimatePresence } from "framer-motion";
import Button from "components/button/Button";
import { useLazyGetCompaniesQuery } from "reduxFiles/api/companyApi";
import { useScope } from "hooks/useScope";
import { Company } from "types/companyTypes";
import SmartSelect from "components/select/Select";
import { useCreateOrganizationAssessmentMutation } from "reduxFiles/api/assessmentApi";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  modal: {
    isOpen: boolean;
    openModal: () => void;
    closeModal: () => void;
    toggleModal: () => void;
  };
  refetch: () => void;
};

const CreateOrganizationAssessmentModal = ({
  isOpen,
  onClose,
  modal,
  refetch,
}: Props) => {
  const { t } = useTranslation();
  const scope = useScope();
  const [companiesCache, setCompaniesCache] = useState<Company[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [selectedValue, setSelectedValue] = useState<string | number | null>(
    null
  );

  // Lazy query for companies
  const [triggerCompanies] = useLazyGetCompaniesQuery();

  // Search companies function
  const searchCompanies = async (text: string) => {
    try {
      const res = await triggerCompanies({
        scope,
        page: 1,
        limit: 50,
        filters: { name: text },
      }).unwrap();

      setCompaniesCache(res.items);

      return res.items.map((company) => ({
        label: company.name,
        value: company.id,
      }));
    } catch (err) {
      console.error(err);
      return [];
    }
  };

  // api
  const [createOrgAssessmentApi, { isLoading: isCreating }] =
    useCreateOrganizationAssessmentMutation();

  const handleCreateOrgAssessment = async () => {
    if (!selectedCompany) {
      return;
    }
    try {
      await createOrgAssessmentApi({
        companyId: selectedCompany.id,
      }).unwrap();

      modal.closeModal();
      refetch();
      setSelectedCompany(null);
      setSelectedValue(null);
      setCompaniesCache([]);
    } catch (err) {
      console.error(err);
    }
  };

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedCompany(null);
      setSelectedValue(null);
      setCompaniesCache([]);
    }
  }, [isOpen]);

  const handleClose = () => {
    setSelectedCompany(null);
    setSelectedValue(null);
    setCompaniesCache([]);
    onClose();
  };

  const handleCompanySelect = (value: string | number | null | undefined) => {
    if (value === null || value === undefined || value === "") {
      setSelectedValue(null);
      setSelectedCompany(null);
      return;
    }

    setSelectedValue(value);

    const company = companiesCache.find((c) => c.id === Number(value));
    if (company) {
      setSelectedCompany(company);
    } else {
      setSelectedCompany(null);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleClose}
          className="bg-black/50 fixed inset-0 backdrop-blur-sm"
        />

        <motion.div
          initial={{ opacity: 0, scale: 0.92, y: 30 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.92, y: 30 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className="relative w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl dark:bg-navy-800"
        >
          {/* Header */}
          <div className="border-b border-gray-100 px-6 py-4 dark:border-white/10">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 p-2 shadow-lg shadow-blue-500/20">
                  <FiCheck className="h-4 w-4 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {t("organizationAssessmentReport.addOrganizationAssessment")}
                </h3>
              </div>
              <button
                onClick={handleClose}
                className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-white/10"
              >
                <FiX className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="space-y-6 p-6">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t("assessment.companyId")}
                <span className="mr-1 text-red-500">*</span>
              </label>

              <SmartSelect
                mode="dynamic"
                onSearch={searchCompanies}
                onChange={handleCompanySelect}
                value={selectedValue}
                minSearchLength={1}
                debounce={300}
                clearable={true}
              />
            </div>

            {selectedCompany && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="rounded-xl bg-gradient-to-r from-blue-50/50 to-purple-50/50 p-4 dark:from-blue-900/10 dark:to-purple-900/10"
              >
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t("organizationAssessmentReport.selectedCompany")}
                </p>
                <p className="mt-1 text-lg font-semibold text-gray-900 dark:text-white">
                  {selectedCompany.name}
                </p>
                {selectedCompany.organization?.name && (
                  <p className="text-sm text-gray-700 dark:text-gray-400">
                    {t("organization.name")}:{" "}
                    {selectedCompany?.organization.name}
                  </p>
                )}
                {selectedCompany.totalStaffCount?.companySize && (
                  <p className="text-sm text-gray-700 dark:text-gray-400">
                    {t("assessment.companySize")}:{" "}
                    {t(
                      `group.sizes.${selectedCompany.totalStaffCount.companySize}`
                    )}
                  </p>
                )}
              </motion.div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-gray-100 bg-gray-50/30 px-6 py-4 dark:border-white/5 dark:bg-white/5">
            <div className="flex gap-3">
              <Button
                onClick={handleClose}
                variant="secondary"
                className="flex-1"
              >
                {t("buttons.cancel")}
              </Button>
              <Button
                onClick={handleCreateOrgAssessment}
                disabled={!selectedCompany || isCreating}
                variant="primary"
                className="flex-1"
                loading={isCreating}
              >
                {t("buttons.create")}
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default CreateOrganizationAssessmentModal;
